// Botón "+" de imagen de referencia para el editor de video con IA (Gemini
// Omni). Replica el "+" del composer del Asistente (mismo look + responsive:
// popover en desktop, bottom-sheet en mobile), acotado a 2 opciones:
//   • "Añadir imagen"   → subir del dispositivo.
//   • "Añadir producto" → elegir del catálogo (reusa `ProductFullMenu`).
//
// Es CONTROLADO: expone la referencia elegida vía `onChange`. UNA sola
// referencia (agregar una nueva reemplaza la anterior) — el editor Omni manda
// una única `reference_image_url`.
//
// La imagen se sube al MISMO bucket que el editor (`video-project-assets`,
// carpeta `agent-refs/`) SIN crear fila en `video_project_assets` → NO aparece
// en el panel "Archivos" (que lee de esa tabla). Reusa `uploadReferenceImage`.
//
// El editor de video vive FUERA de los providers de InterfazV2, así que para
// "Añadir producto" fetcheamos los productos por `companyId` y proveemos el
// `ProductsContext` localmente alrededor del picker.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Upload, Package, ChevronRight, Loader2 } from 'lucide-react';
import {
  uploadReferenceImage,
  type ChatAttachment,
} from '../pages/v2/agentAttachments';
import { ProductFullMenu } from '../pages/v2/ChatPanelV2';
import {
  ProductsContext,
  ProductsLoadedContext,
} from '../pages/v2/productsContext';
import { fetchUserProducts } from '../pages/v2/productsApi';
import { useRubroMarcaActiva } from '../hooks/useRubroMarca';
import type { MockProductV2 } from '../pages/v2/mockData';
import { ModalDeFuentes } from '../chat-atlas/ui/ModalDeFuentes';

interface OmniRefAttachProps {
  companyId: string;
  value: ChatAttachment | null;
  onChange: (ref: ChatAttachment | null) => void;
  disabled?: boolean;
  /**
   * El "+" abre el MISMO selector que el "Agregar" del agente (`ModalDeFuentes`:
   * productos, creaciones, subidas, modelos, poses, fondos) y lo elegido queda como
   * referencia, en vez del menú corto de dos opciones. Lo usa "Editar clip" del
   * Estudio; el historial sigue con el menú de siempre
   * (ADR-2026-09-27-editar-clip-agrega-con-el-selector-del-agente).
   */
  conSelectorDelAgente?: boolean;
}

export const OmniRefAttach: React.FC<OmniRefAttachProps> = ({
  companyId,
  value,
  onChange,
  disabled = false,
  conSelectorDelAgente = false,
}) => {
  const [fuentesAbiertas, setFuentesAbiertas] = useState(false);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProducts, setShowProducts] = useState(false);
  const [products, setProducts] = useState<MockProductV2[]>([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch de productos (para "Añadir producto"). El editor no tiene el provider
  // de InterfazV2, así que lo traemos por companyId.
  //
  // Y por la MARCA ACTIVA (más los huérfanos, ADR-018), como el resto de la app —
  // ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat: era el último punto que ofrecía
  // el catálogo entero de la empresa. El hook trae la marca con el desempate canónico y
  // escucha `atlas:brand-changed`, así que el fetch se repite solo al cambiar de marca.
  const { marcaId, loaded: marcaResuelta } = useRubroMarcaActiva();
  useEffect(() => {
    // Con la marca sin resolver, `fetchUserProducts` no filtraría: se espera para no
    // llenar la lista con los productos de las otras marcas y corregirla un instante
    // después.
    if (!marcaResuelta) return;
    let cancelled = false;
    fetchUserProducts(companyId, marcaId)
      .then((p) => {
        if (cancelled) return;
        setProducts(p);
        setProductsLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setProductsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, marcaId, marcaResuelta]);

  // Cerrar el menú al clickear afuera (ancla desktop + sheet mobile marcados con
  // [data-omni-attach]).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest('[data-omni-attach]')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);

  const pickImage = async (file: File | null) => {
    if (!file) return;
    setOpen(false);
    setUploading(true);
    setError(null);
    try {
      const { storagePath, previewUrl } = await uploadReferenceImage(
        file,
        companyId,
      );
      onChange({ kind: 'image', id: crypto.randomUUID(), previewUrl, storagePath });
    } catch (e: any) {
      setError(e?.message || 'No se pudo subir la imagen.');
    } finally {
      setUploading(false);
    }
  };

  const acceptProduct = (ids: string[]) => {
    setShowProducts(false);
    const id = ids[0];
    if (!id) return;
    const prod = products.find((p) => p.id === id);
    if (!prod) return;
    onChange({
      kind: 'product',
      id: crypto.randomUUID(),
      productId: prod.id,
      name: prod.name,
      imageUrl: prod.imageUrl,
    });
  };

  // Contenido del menú (compartido desktop popover / mobile sheet).
  const menu = (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-sm transition-colors"
      >
        <Upload className="w-4 h-4" />
        Añadir imagen
      </button>
      <button
        type="button"
        onClick={() => {
          setOpen(false);
          // Limpiar un posible error de subida previo: no debe quedar pegado
          // cuando el usuario cambia a elegir un producto.
          setError(null);
          setShowProducts(true);
        }}
        className="w-full flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-neutral-100 active:bg-neutral-100 transition-colors text-left"
      >
        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-neutral-100 text-brand-primary shrink-0">
          <Package className="w-4 h-4" />
        </span>
        <span className="flex-1 text-[14px] text-brand-black/90">
          Añadir producto
        </span>
        <ChevronRight className="w-4 h-4 text-neutral-400" />
      </button>
    </div>
  );

  return (
    <div className="relative shrink-0" data-omni-attach>
      <button
        type="button"
        onClick={() =>
          conSelectorDelAgente ? setFuentesAbiertas(true) : setOpen((v) => !v)
        }
        disabled={disabled || uploading}
        aria-label="Adjuntar imagen de referencia"
        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 disabled:opacity-50 text-brand-black/70 transition-colors"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Plus className="w-4 h-4" strokeWidth={2.25} />
        )}
      </button>

      {/* Desktop: popover anclado al botón, se abre hacia arriba. */}
      {open && (
        <div className="hidden md:block absolute bottom-full left-0 mb-2 w-[260px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-2 z-20">
          {menu}
        </div>
      )}

      {/* Mobile: bottom-sheet portaleado a body (escapa el stacking context). */}
      {open &&
        createPortal(
          <div className="md:hidden fixed inset-0 z-[9998]" data-omni-attach>
            <div
              className="absolute inset-0 bg-black/40 animate-fade-in"
              onClick={() => setOpen(false)}
            />
            <div
              className="absolute inset-x-0 bottom-0 bg-white rounded-t-[20px] shadow-2xl p-3 animate-slide-up-sheet"
              style={{
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto mb-3" />
              {menu}
            </div>
          </div>,
          document.body,
        )}

      {/* Input de archivo oculto (lo dispara "Añadir imagen"). */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          pickImage(f);
          e.target.value = '';
        }}
      />

      {/* Picker de producto (reusa ProductFullMenu del asistente). Proveemos el
          contexto de productos que fetcheamos por companyId. El portal cambia el
          DOM pero el Provider sigue envolviéndolo en el árbol de React. */}
      {showProducts &&
        createPortal(
          <ProductsContext.Provider value={products}>
            <ProductsLoadedContext.Provider value={productsLoaded}>
              <ProductFullMenu
                initialSelected={
                  value?.kind === 'product' ? [value.productId] : []
                }
                allowMulti={false}
                onCancel={() => setShowProducts(false)}
                onAccept={acceptProduct}
              />
            </ProductsLoadedContext.Provider>
          </ProductsContext.Provider>,
          document.body,
        )}

      {conSelectorDelAgente && (
        <ModalDeFuentes
          abierto={fuentesAbiertas}
          onCerrar={() => setFuentesAbiertas(false)}
          onAdjuntar={(a) => {
            setError(null);
            onChange(a);
          }}
        />
      )}

      {error && (
        <div className="absolute bottom-full left-0 mb-2 w-[240px] bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-[12px] text-red-600 z-20">
          {error}
        </div>
      )}
    </div>
  );
};
